import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Favorite as FavoriteIcon,
  Restaurant as RestaurantIcon,
} from "@mui/icons-material";
import api from "../../services/api";
import TipDialog from "./TipDialog";

const OrderStatus = () => {
  const { storeId, orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [tipDialogOpen, setTipDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    fetchOrder();
    fetchStaff();
    // ポーリング不要（シンプル版）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const fetchStaff = async () => {
    try {
      const res = await api.get(`/api/public/stores/${storeId}/staff`);
      setStaff(res.data || []);
    } catch (err) {
      console.error("Failed to fetch staff:", err);
    }
  };

  const fetchOrder = async () => {
    try {
      const res = await api.get(`/api/public/orders/${orderId}`);
      setOrder(res.data);
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch order:", err);
      const message = err.response?.data?.message || err.message || "注文情報の取得に失敗しました";
      setError(message);
      setLoading(false);
    }
  };

  const handleOpenTipDialog = (staffMember) => {
    setSelectedStaff(staffMember);
    setTipDialogOpen(true);
  };

  const handleCloseTipDialog = () => {
    setTipDialogOpen(false);
    setSelectedStaff(null);
  };

  const handleSubmitTip = async (tipData) => {
    try {
      // 注文のsession_idを取得（投げ銭と注文を紐付ける）
      const sessionId = order?.session_id || localStorage.getItem(`session_${storeId}`);
      await api.post(`/api/public/stores/${storeId}/tips`, {
        staffId: tipData.staffId,
        amount: tipData.amount,
        message: tipData.message,
        sessionId,
      });
      setSuccessMessage("チップを送信しました。ありがとうございます！");
      setShowSuccess(true);
      handleCloseTipDialog();
    } catch (err) {
      console.error("Failed to send tip:", err);
      throw err;
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigate(`/store/${storeId}/menu`)}
        >
          メニューに戻る
        </Button>
      </Container>
    );
  }

  if (!order) return null;

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      {/* 注文完了メッセージ */}
      <Paper elevation={3} sx={{ p: 4, mb: 3, textAlign: "center" }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          ご注文ありがとうございます
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          注文を受け付けました。しばらくお待ちください。
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="order-id"
        >
          注文番号: {(order._id || order.id || "").slice(-8).toUpperCase()}
        </Typography>
      </Paper>

      {/* 注文内容 */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          注文内容
        </Typography>
        <List disablePadding>
          {(order.items || []).map((item, index) => (
            <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
              <ListItemText
                primary={item.name}
                secondary={`× ${item.quantity}`}
                primaryTypographyProps={{ variant: "body2" }}
                secondaryTypographyProps={{ variant: "caption" }}
              />
              <Typography variant="body2">
                ¥{(item.price * item.quantity).toLocaleString()}
              </Typography>
            </ListItem>
          ))}
        </List>
        <Divider sx={{ my: 1.5 }} />
        <Box display="flex" justifyContent="space-between">
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            合計
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            ¥{(order.totalAmount || order.total_amount || 0).toLocaleString()}
          </Typography>
        </Box>
      </Paper>

      {/* 追加注文ボタン */}
      <Button
        variant="contained"
        fullWidth
        size="large"
        startIcon={<RestaurantIcon />}
        onClick={() => {
          // セッション情報を引き継いでメニューに戻る
          const tableNumber = order?.table_sessions?.table_number || order?.tableNumber;
          const area = order?.table_sessions?.area || order?.area;
          const sessionId = order?.session_id;

          let url = `/store/${storeId}/menu`;
          const params = new URLSearchParams();
          if (tableNumber) params.set('table', tableNumber);
          if (area) params.set('area', area);
          if (sessionId) params.set('sessionId', sessionId);

          if (params.toString()) {
            url += `?${params.toString()}`;
          }
          navigate(url);
        }}
        sx={{ mb: 2, py: 1.5 }}
      >
        追加注文する
      </Button>

      {/* 投げ銭セクション */}
      {staff.length > 0 && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="subtitle1" gutterBottom align="center" sx={{ fontWeight: 600 }}>
            <FavoriteIcon sx={{ verticalAlign: "middle", mr: 1, color: "#e91e63", fontSize: 20 }} />
            スタッフに投げ銭
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            align="center"
            display="block"
            sx={{ mb: 2 }}
          >
            感謝の気持ちを伝えましょう
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {staff.map((staffMember) => (
              <Button
                key={staffMember._id || staffMember.id}
                variant="outlined"
                size="small"
                onClick={() => handleOpenTipDialog(staffMember)}
                sx={{ flex: "1 1 calc(50% - 4px)", minWidth: "120px" }}
              >
                {staffMember.name?.ja || staffMember.name}
              </Button>
            ))}
          </Box>
        </Paper>
      )}

      <TipDialog
        open={tipDialogOpen}
        onClose={handleCloseTipDialog}
        staff={selectedStaff}
        onSubmit={handleSubmitTip}
      />

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        message={successMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Container>
  );
};

export default OrderStatus;
